import { Dispatch, SetStateAction, useMemo } from "react";
import { FourDPQuiz } from "@WahooFitness/cloud-client-ts";
import {
  FourDPQuizResultsType,
  TrainingProgressionUpdateType,
  UserType,
} from "@WahooFitness/cloud-client-types";
import { useCloudContext, useConfigContext, useOfflineSWR } from "@WahooFitness/wahoo-offline-mfe";
import { SourcePage } from "@/services/Analytics/EventTypes";
import useQuiz from "@/components/Quiz/useQuiz";
import useQuizOperations from "@/components/Quiz/useQuizOperations";
import FourDPPages from "./FourDPPages";
import useTrainingProgressEstimate from "./useTrainingProgressEstimate";

function useFourDPQuiz(
  user: UserType,
  setResults: Dispatch<SetStateAction<FourDPQuizResultsType | undefined>>,
  setPage: Dispatch<SetStateAction<number>>,
  setError: Dispatch<SetStateAction<string | undefined>>,
  skipEducation: boolean,
  setInitialTrainingProgression: Dispatch<
    SetStateAction<TrainingProgressionUpdateType | undefined>
  >,
  onFirstStepBack?: () => void
) {
  const { getCloudClient } = useCloudContext();
  const { wahooToken, locale } = useConfigContext();
  const { createTrainingProgressEstimateFromResponses } = useTrainingProgressEstimate();

  const quizClient = useMemo(() => getCloudClient(FourDPQuiz), [getCloudClient]);

  const {
    data: quizData,
    isLoading: quizLoading,
    error: quizError,
  } = useOfflineSWR(["fourDPquizQuestions", locale], () => quizClient.get(wahooToken, locale));

  const { getQuizResults, buildResponse } = useQuizOperations<FourDPQuizResultsType>(quizClient);

  return useQuiz(user, setResults, setPage, setError, skipEducation, {
    quizData,
    quizLoading,
    quizError,
    getQuizResults,
    buildResponse,
    pages: FourDPPages,
    analyticsEventName: "4dp_quiz",
    analyticsSourcePage: SourcePage.fourdp_quiz,
    onBeforeSubmit: (responses) => {
      setInitialTrainingProgression(createTrainingProgressEstimateFromResponses(responses));
    },
    onFirstStepBack,
  });
}

export default useFourDPQuiz;
